import { useState, useEffect } from 'react'
import { doc, collection, onSnapshot, type DocumentData } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useAuth } from '../contexts/AuthContext'
import type { DocumentScanStatus, DocumentClassification } from '../types/documentScanning'

/**
 * Case document as stored in Firestore subcollection: cases/{caseId}/documents/{documentId}
 * Now includes classification fields for single source of truth.
 */
export interface CaseDocument {
  id: string
  filename: string
  storagePath?: string
  downloadUrl?: string  // Pre-signed URL from backend - use this for viewing
  contentType?: string
  size?: number
  uploadedAt?: Date
  // Classification fields (populated by scan workflow)
  document_type?: string
  document_subtype?: string
  tax_year?: number
  confidence?: number
  gcs_path?: string
  summary?: string
  key_information?: Record<string, unknown>
  quality_score?: number
  quality_issues?: string[]
}

/**
 * Document scan progress info
 */
export interface DocumentScanProgress {
  current: number
  total: number
  percentage: number
}

/**
 * Current step info for document scanning
 */
export interface DocumentScanCurrentStep {
  phase: string
  message: string
  filename?: string
  timestamp?: string
}

export interface Case {
  id: string
  uid: string
  borrowerName: string
  loanType: string
  status: string
  documents: CaseDocument[]  // From subcollection, includes classification fields
  documentsCount?: number    // Denormalized count for UI
  documentScan?: {
    status: DocumentScanStatus
    statusMessage?: string  // Humanized status message from webhook
    progress?: DocumentScanProgress  // Progress info {current, total, percentage}
    currentStep?: DocumentScanCurrentStep  // Current step details
    // Note: activityFeed is now in cases/{caseId}/activity subcollection - use useActivityFeed hook
    workflowId?: string
    error?: string
  }
  incomeVerification?: {
    status: string
    currentPhase: number
    workflowId?: string
  }
  createdAt: Date
  updatedAt: Date
}

interface UseCaseResult {
  caseData: Case | null
  loading: boolean
  error: Error | null
}

/**
 * Hook to subscribe to a single case from Firestore, including its documents subcollection.
 */
export function useCase(caseId: string | undefined): UseCaseResult {
  const { user } = useAuth()
  const [caseData, setCaseData] = useState<Case | null>(null)
  const [documents, setDocuments] = useState<CaseDocument[]>([])
  const [caseBaseData, setCaseBaseData] = useState<DocumentData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // Subscribe to case document
  useEffect(() => {
    if (!user || !caseId) {
      setCaseData(null)
      setCaseBaseData(null)
      setDocuments([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const caseRef = doc(db, 'cases', caseId)

    const unsubscribe = onSnapshot(
      caseRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setError(new Error('Case not found'))
          setCaseBaseData(null)
          setLoading(false)
          return
        }

        const data = snapshot.data()

        // Verify ownership
        if (data.uid !== user.uid) {
          setError(new Error('Access denied'))
          setCaseBaseData(null)
          setLoading(false)
          return
        }

        setCaseBaseData(data)
        setLoading(false)
      },
      (err) => {
        console.error('Error fetching case:', err)
        setError(err)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [user, caseId])

  // Subscribe to documents subcollection
  useEffect(() => {
    if (!user || !caseId) {
      setDocuments([])
      return
    }

    console.log('useCase: Subscribing to documents subcollection for case:', caseId)
    const docsRef = collection(db, 'cases', caseId, 'documents')

    const unsubscribe = onSnapshot(
      docsRef,
      (snapshot) => {
        console.log('useCase: Documents snapshot received, count:', snapshot.docs.length)
        const docs: CaseDocument[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data()
          return {
            id: docSnap.id,
            filename: data.filename || 'Unknown',
            storagePath: data.storage_path || data.storagePath,
            downloadUrl: data.download_url || data.downloadUrl,
            contentType: data.content_type || data.contentType,
            size: data.file_size || data.size,
            uploadedAt: data.uploaded_at?.toDate?.() || data.uploadedAt?.toDate?.() || new Date(),
            // Classification fields
            document_type: data.document_type,
            document_subtype: data.document_subtype,
            tax_year: data.tax_year,
            confidence: data.confidence,
            gcs_path: data.gcs_path,
            summary: data.summary,
            key_information: data.key_information,
            quality_score: data.quality_score,
            quality_issues: data.quality_issues,
          }
        })
        // Sort client-side by uploaded_at descending
        docs.sort((a, b) => (b.uploadedAt?.getTime() || 0) - (a.uploadedAt?.getTime() || 0))
        setDocuments(docs)
      },
      (err) => {
        console.error('useCase: Error fetching documents subcollection:', err)
        // Don't set error - documents are optional, case might just be empty
      }
    )

    return () => unsubscribe()
  }, [user, caseId])

  // Combine case base data with documents into full Case object
  useEffect(() => {
    if (!caseId || !caseBaseData) {
      setCaseData(null)
      return
    }

    setCaseData(transformCaseData(caseId, caseBaseData, documents))
  }, [caseId, caseBaseData, documents])

  return { caseData, loading, error }
}

/**
 * Map a CaseDocument to DocumentClassification.
 * Returns null if the document doesn't have a document_type.
 */
export function mapCaseDocumentToClassification(
  doc: CaseDocument
): DocumentClassification | null {
  if (!doc.document_type) {
    return null
  }

  return {
    filename: doc.filename,
    document_type: doc.document_type,
    document_subtype: doc.document_subtype,
    confidence: doc.confidence || 0,
    tax_year: doc.tax_year,
    gcs_path: doc.gcs_path,
    summary: doc.summary,
    key_information: doc.key_information,
    quality_score: doc.quality_score,
    quality_issues: doc.quality_issues,
  }
}

/**
 * Transform Firestore document to Case type
 */
function transformCaseData(id: string, data: DocumentData, documents: CaseDocument[]): Case {
  const borrowerInfo = data.borrower_info || data.borrower || {}
  const borrowerName = borrowerInfo.full_name || borrowerInfo.fullName || 'Unknown Borrower'
  const loanType = borrowerInfo.loan_type || data.loan_type || 'fannie_mae'

  // Transform document scan data (orchestration only)
  // Note: activityFeed is now in cases/{caseId}/activity subcollection
  const documentScan = data.documentScan ? {
    status: data.documentScan.status || 'idle',
    statusMessage: data.documentScan.statusMessage,
    progress: data.documentScan.progress,
    currentStep: data.documentScan.currentStep,
    workflowId: data.documentScan.workflowId || data.documentScan.workflow_id,
    error: data.documentScan.error,
  } : undefined

  // Transform income verification data
  const incomeVerification = data.incomeVerification ? {
    status: data.incomeVerification.status || 'pending',
    currentPhase: data.incomeVerification.currentPhase || 0,
    workflowId: data.incomeVerification.workflowId || data.incomeVerification.workflow_id,
  } : undefined

  return {
    id,
    uid: data.uid,
    borrowerName,
    loanType,
    status: data.status || 'created',
    documents,  // From subcollection subscription
    documentsCount: data.documentsCount || documents.length,
    documentScan,
    incomeVerification,
    createdAt: data.createdAt?.toDate?.() || new Date(),
    updatedAt: data.updatedAt?.toDate?.() || new Date(),
  }
}
